import { Search } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import { useCallback, useRef, useState } from "react";
import type { TextInput } from "react-native";
import { Text, useDebounce, View, YStack } from "tamagui";
import { useStores } from "../../../core/stores";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Input, type InputProps } from "../../../ui/InputNew";
import { Label } from "../../../ui/Label";
import { Subtext } from "../../../ui/Subtext";

type PlacesAutoCompleteInputProps = {
  onSelect?: (location: google.maps.places.PlacePrediction) => void;
  locationBias?: { lat: number; lng: number };
} & InputProps;

const formatText = (
  formattableText?: google.maps.places.FormattableText | null,
) => {
  if (!formattableText) {
    return null;
  }

  const { text, matches } = formattableText;
  const elements: React.ReactNode[] = [];
  let lastIndex = 0;

  matches.forEach((match, index) => {
    if (lastIndex < match.startOffset) {
      elements.push(
        <Body key={`normal-${index}`}>
          {text.slice(lastIndex, match.startOffset)}
        </Body>,
      );
    }
    elements.push(
      <Label key={`bold-${index}`}>
        {text.slice(match.startOffset, match.endOffset)}
      </Label>,
    );
    lastIndex = match.endOffset;
  });

  if (lastIndex < text.length) {
    elements.push(<span key={"normal-end"}>{text.slice(lastIndex)}</span>);
  }

  return <Text>{elements}</Text>;
};

export const PlacesAutoCompleteInput: React.FC<PlacesAutoCompleteInputProps> =
  observer(({ onSelect, locationBias, ...inputProps }) => {
    const { sessionStore } = useStores();
    const [locations, setLocations] = useState<
      google.maps.places.PlacePrediction[] | undefined
    >();
    const lastSearch = useRef("");
    const [showResults, setShowResults] = useState(false);
    const inputRef = useRef<TextInput>(null);

    const [isLoadingAutoComplete, setIsLoadingAutoComplete] = useState(false);

    const handleInjectAutoComplete = useCallback(
      async (searchString: string) => {
        // Early out if it's the same search string
        if (locations && lastSearch.current === searchString) {
          return;
        }

        setIsLoadingAutoComplete(true);
        setLocations(undefined);

        if (searchString) {
          const { AutocompleteSessionToken, AutocompleteSuggestion } =
            (await google.maps.importLibrary(
              "places",
            )) as google.maps.PlacesLibrary;

          const request: google.maps.places.AutocompleteRequest = {
            input: searchString,
            locationBias,
            sessionToken: sessionStore.getGoogleAutocompleteToken(
              AutocompleteSessionToken,
            ),
          };

          // Fetch autocomplete suggestions.
          const { suggestions } =
            await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

          lastSearch.current = searchString;

          setLocations(
            suggestions
              .filter(
                (
                  s,
                ): s is {
                  placePrediction: google.maps.places.PlacePrediction;
                } => !!s.placePrediction,
              )
              .map((s) => s.placePrediction),
          );
        }
        setIsLoadingAutoComplete(false);
      },
      [locationBias, sessionStore, locations],
    );

    const debouncedHandleInjectAutoComplete = useDebounce(
      handleInjectAutoComplete,
      1000,
    );

    return (
      <>
        <Input
          ref={inputRef}
          icon={Search}
          {...inputProps}
          isLoading={inputProps.isLoading || isLoadingAutoComplete}
          submitOnEnter
          preventNewLine
          onFocus={() => setShowResults(true)}
          onSubmitEditing={(e) => {
            handleInjectAutoComplete(e.nativeEvent.text);
          }}
          onChangeText={(searchString) => {
            // Early out if it's the same search string
            if (locations && lastSearch.current === searchString) {
              return;
            }

            setIsLoadingAutoComplete(true);

            debouncedHandleInjectAutoComplete(searchString);
          }}
        />
        {showResults && locations?.length ? (
          <YStack paddingTop={1} gap={1}>
            {locations.map((location) => (
              <Box
                cursor="pointer"
                onPress={() => {
                  setShowResults(false);
                  onSelect?.(location);

                  inputRef.current?.clear();
                }}
                key={location.placeId}
              >
                <View>
                  {formatText(location.mainText)}
                  <Subtext>{formatText(location.secondaryText)}</Subtext>
                </View>
              </Box>
            ))}
          </YStack>
        ) : undefined}
      </>
    );
  });
